// V5.20 : ErrorBoundary pour gérer les erreurs de données corrompues
// Redirige vers l'étape 1 en cas de données corrompues dans le cache
import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // V5.20 : Nettoyer les données corrompues du localStorage si c'est une erreur de parsing
    if (error.message?.includes('JSON') || error.message?.includes('undefined') || error.message?.includes('null')) {
      this.cleanupCorruptedData();
    }
  }

  cleanupCorruptedData = () => {
    // Nettoyer les clés de mission potentiellement corrompues
    const keysToClean = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('mission_')) {
        keysToClean.push(key);
      }
    }
    keysToClean.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        JSON.parse(value); // Test si c'est du JSON valide
      } catch {
        console.warn(`Cleaning corrupted data: ${key}`);
        localStorage.removeItem(key);
      }
    });
  };

  handleReset = () => {
    // Nettoyer toutes les données de mission
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('mission_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Rediriger vers l'étape 1
    this.setState({ hasError: false, error: null, errorInfo: null });
    
    // Forcer un rechargement de la page
    if (this.props.onReset) {
      this.props.onReset();
    } else {
      window.location.href = '/choix-mission';
    }
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-void flex items-center justify-center p-4">
          <div className="max-w-md w-full glass-card p-8 border border-red-500/30 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            
            <h2 className="font-orbitron text-xl font-bold text-white mb-3">
              Erreur détectée
            </h2>
            
            <p className="text-gray-400 mb-4">
              Une erreur s'est produite lors du chargement de votre mission.
              Cela peut être dû à des données corrompues dans le cache.
            </p>

            {/* DDD.2 — Affichage erreur réelle pour debug rapide (prod inclus) */}
            {this.state.error && (
              <div className="mb-6 p-3 bg-red-500/5 border border-red-500/20 rounded text-left">
                <p className="text-red-400 text-xs font-mono mb-2 uppercase tracking-wider">// ERREUR_TECHNIQUE</p>
                <p className="text-gray-300 text-xs font-mono break-all">
                  {this.state.error.message || String(this.state.error)}
                </p>
                <button
                  onClick={() => {
                    const text = `Mission Error\nMessage: ${this.state.error?.message || this.state.error}\nStack: ${this.state.error?.stack || 'N/A'}\nURL: ${window.location.href}`;
                    navigator.clipboard?.writeText(text);
                    alert('Erreur copiée. Colle-la dans le chat avec Claude pour diagnostic.');
                  }}
                  className="mt-2 text-cyan-400 text-xs underline hover:text-cyan-300"
                >
                  Copier l'erreur
                </button>
              </div>
            )}
            
            <div className="space-y-3">
              <Button
                onClick={this.handleReset}
                className="w-full bg-cyan-500 text-black hover:bg-cyan-400 font-orbitron"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Recommencer la mission
              </Button>
              
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full border-gray-600 text-gray-300 hover:text-white"
              >
                <Home className="w-4 h-4 mr-2" />
                Retour au Dashboard
              </Button>
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-6 p-3 bg-gray-800 rounded text-left">
                <p className="text-red-400 text-xs font-mono break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
